import { Maximize2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { countPill } from '../ui/urgency-rail.ts';

/** Tall enough for three collapsed notes (three lines and "Read more" each); then it scrolls. */
export const BLOCK_LIST_CLASS = 'max-h-[29rem] overflow-y-auto pr-1';

/**
 * A notes or tasks card (project Overview and Jobs tab, an expanded job), with "Full screen": a large dialog holding only that
 * block, its list uncapped. `children(full)` draws the block for either place. `filter` and
 * `action` sit before the "Full screen" icon (the project's notes: origin filter and Add note);
 * both are in the full-screen header too, and their state is the caller's, so it carries over.
 */
export function BlockCard({
  title,
  count,
  filter,
  action,
  toolbar,
  fill = false,
  children,
}: {
  title: string;
  /** How many items the card lists, as a pill beside the title. */
  count?: number | undefined;
  filter?: ReactNode;
  action?: ReactNode;
  /** A row of its own under the title line (Team Tasks' task filters), here and in full screen. */
  toolbar?: ReactNode;
  /**
   * From `lg` up, take the height the parent gives it and scroll inside (Home and Team Tasks).
   * Narrower, the cards stack: each is as tall as its content, up to a cap, then scrolls.
   */
  fill?: boolean;
  children: (full: boolean) => ReactNode;
}) {
  const [full, setFull] = useState(false);
  return (
    <Card
      className={fill ? 'flex min-h-0 flex-col p-4 sm:p-5 lg:h-full' : 'p-5'}
      aria-label={title}
    >
      {/* Top-aligned: the filter's markers under its toggles never shift the title or buttons. */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <h3 className="flex shrink-0 items-center gap-1.5 text-sm leading-7 font-semibold whitespace-nowrap">
          {title}
          {count !== undefined && (
            <span className={countPill} data-testid="block-count">
              {count}
            </span>
          )}
        </h3>
        <div className="ml-auto flex flex-wrap items-start justify-end gap-2">
          {filter}
          {action}
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setFull(true)}
            aria-label={`Full screen ${title.toLowerCase()}`}
            title="Full screen"
            data-testid="block-full-screen"
          >
            <Maximize2 />
          </Button>
        </div>
      </div>
      {toolbar && <div className="mb-3">{toolbar}</div>}
      {fill ? (
        // `relative`: Radix Select's hidden native <select> is absolute; without it it escapes the scroll.
        <div className="relative max-h-[36rem] min-h-0 overflow-y-auto lg:max-h-none lg:flex-1">
          {children(false)}
        </div>
      ) : (
        children(false)
      )}
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent
          className="flex h-[calc(100dvh-4rem)] max-w-5xl flex-col"
          aria-describedby={undefined}
          // Radix would focus the first control (the toggle's All), which then reads as chosen;
          // the dialog itself takes focus instead, so the keyboard starts inside it.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).focus();
          }}
          data-testid="block-full-screen-dialog"
        >
          <DialogHeader className="flex-row flex-wrap items-start gap-2">
            <DialogTitle>{title}</DialogTitle>
            {(filter || action) && (
              <div className="ml-auto flex flex-wrap items-start justify-end gap-2">
                {filter}
                {action}
              </div>
            )}
          </DialogHeader>
          {toolbar}
          {/* Grows into the rest of the dialog, so a card can fill it; taller content still scrolls it. */}
          {full && <div className="flex flex-auto flex-col">{children(true)}</div>}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
